import { Link } from "@tanstack/react-router";
import { BookOpen, ShoppingCart, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trackEvent } from "@/lib/analytics";
import { appCheckFetch } from "@/lib/firebase";

import { useCart } from "../cart/CartContext";
import type { Product } from "../catalog/types";
import { useCurrency } from "../currency/CurrencyContext";

interface ProductRating {
  averageRating: number;
  reviewCount: number;
}

export function ProductCard({ product }: { product: Product }) {
  const { add } = useCart();
  const { formatPrice } = useCurrency();
  const [rating, setRating] = useState<ProductRating>();
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardCover = product.coverThumbnail ?? product.coverImage;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const params = new URLSearchParams({ productId: product.id });

    const loadRating = () => {
      void appCheckFetch(`/api/product-engagement?${params}`, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      })
        .then(async (response) => {
          const data = (await response.json().catch(() => null)) as {
            summary?: ProductRating;
          } | null;
          if (!response.ok || !data?.summary || data.summary.reviewCount < 1) return;
          if (active) setRating(data.summary);
        })
        .catch(() => {
          // La tarjeta sigue funcionando aunque la calificación no esté disponible.
        });
    };

    const card = cardRef.current;
    if (!card || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      loadRating();
      return () => controller.abort();
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setIsVisible(true);
      observer.disconnect();
      loadRating();
    });
    observer.observe(card);

    return () => {
      active = false;
      observer.disconnect();
      controller.abort();
    };
  }, [product.id]);

  return (
    <Card
      ref={cardRef}
      className={`group flex flex-col overflow-hidden rounded-xl border bg-white p-0 transition-colors hover:border-black ${
        product.featured ? "border-black" : "border-black/15"
      }`}
    >
      <Link
        to="/productos/$productId"
        params={{ productId: product.slug }}
        className="block"
        aria-label={`Ver ${product.title}`}
      >
        <div className="relative h-[220px] overflow-hidden bg-white p-2 lg:h-60">
          {cardCover && isVisible ? (
            <>
              {product.featured && (
                <span className="absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.1em] text-black shadow-sm">
                  <Star className="h-3 w-3 fill-current" />
                  Destacado
                </span>
              )}
              <span
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-75 blur-xl brightness-75"
                style={{
                  backgroundImage: `url("${cardCover}")`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }}
              />
              <div className="relative mx-auto aspect-[210/297] h-full overflow-hidden rounded-[5px]">
                <img
                  src={cardCover}
                  alt={product.title}
                  className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25">
                <BookOpen className="h-6 w-6" />
              </span>
            </div>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug tracking-[-0.025em] text-black">
          {product.title}
        </h3>
        <div className="mt-3">
          <span className="block text-sm font-semibold text-black">
            {formatPrice(product.price, product.currency)}
          </span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="mt-0.5 block text-xs text-black/70">
              Antes{" "}
              <span className="line-through">
                {formatPrice(product.originalPrice, product.currency)}
              </span>
            </span>
          )}
          {rating && (
            <span
              className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-black/75"
              aria-label={`${rating.averageRating.toLocaleString("es-CO", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })} de 5 estrellas`}
            >
              {rating.averageRating.toLocaleString("es-CO", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
            </span>
          )}
        </div>
        <div className="mt-auto flex gap-2 pt-3">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="flex-1 rounded-lg border-black bg-white text-black hover:bg-black hover:text-white"
          >
            <Link to="/productos/$productId" params={{ productId: product.slug }}>
              Ver libro
            </Link>
          </Button>
          <Button
            size="sm"
            className="flex-1 rounded-lg border border-black bg-black text-white shadow-none hover:bg-white hover:text-black"
            onClick={() => {
              add(product.id);
              void trackEvent("add_to_cart", {
                currency: product.currency,
                value: product.price,
                item_id: product.id,
                item_name: product.title,
                items: [
                  {
                    item_id: product.id,
                    item_name: product.title,
                    price: product.price,
                    quantity: 1,
                  },
                ],
              });
              toast.success("Añadido al carrito");
            }}
          >
            <ShoppingCart className="h-4 w-4 mr-1.5" /> Añadir
          </Button>
        </div>
      </div>
    </Card>
  );
}
